import { Link } from '@mui/material'
import { $Enums, NewContract } from '@vae/server/dist/client'
import { ProductConsumptionCosts } from '@vae/server/src/modules/ExternalAPIs/Synerall/utils/costCalculators'
import dayjs from 'dayjs'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { logEvent } from '../../../analytics/utils/firebaseUtils'
import Button from '../../../components/actions/Button'
import CheckboxInput from '../../../components/inputs/CheckboxInput'
import { ModalContext } from '../../../contexts/ModalContext'
import i18n from '../../../i18n'
import { trpc } from '../../../modules/trpc'

const TermsAndConditionsStep: React.FC<{
	contract: Partial<NewContract>
	setContract: (key: keyof NewContract, value: string | number | boolean) => void
	termsAccepted: boolean
	setTermsAccepted: any
	offer: ProductConsumptionCosts | undefined
	onComplete?: () => void
}> = ({ contract, setContract, termsAccepted, setTermsAccepted, offer, onComplete }) => {
	const { insertModal, clearModals } = useContext(ModalContext)
	const { mutate: createNewContract, isLoading } =
		trpc.adminPanel.supplyContracts.createMobile.useMutation({
			// conversionValue (CLV) is calculated based on these assumptions
			// NPS product (margin exists):
			// 1) average consumption is 6500Kwh for nps products
			// 2) average contract active time 12months
			//
			// FIXED product (basePrice exists):
			// 1) average profit margin per fixed term is 0.7 snt/kWh
			// 2) average consumption is 4500kWh for fixed term products
			// 3) average contract active time 12 months
			//
			// NOTE: if there are non-fixed term products with basePrice, then those are currently calculated with fixed product CLV calculation

			onSuccess: async (createdContractID: number) => {
				const product = offer?.product

				if (product) {
					const basePrice = product.Prices.find(element => element.PriceType === 'Base')?.Price ?? 0
					const margin = product.Prices.find(element => element.PriceType === 'Margin')?.Price ?? 0
					const monthlyPrice =
						product.Prices.find(element => element.PriceType === 'Monthly')?.Price ?? 0

					const value =
						margin && monthlyPrice
							? (margin * 6500) / 1000 + monthlyPrice * 12
							: basePrice && monthlyPrice
								? (0.7 * 4500) / 1000 + monthlyPrice * 12
								: null

					await logEvent('purchase', {
						affiliation: 'mobile',
						currency: 'EUR',
						start_date: dayjs(contract.contractStartDate).format('YYYY-MM-DD'),
						end_date: product.ContractTermMonths
							? dayjs(contract.contractStartDate).add(product.ContractTermMonths, 'months')
							: undefined,
						item_id: contract.productId?.toString(),
						items: [contract.productId?.toString(), contract.lockInProductId?.toString()],
						transaction_id: createdContractID.toString(),
						value: value,
					})

					clearModals()
					insertModal('contractConfirmation')
				}
			},
		})

	const onCreateContract = async () => {
		const product = offer?.product

		if (product) {
			await logEvent('click-create-contract', offer)

			const productName = product?.Titles.find(
				(x: any) => x.Lang.toLowerCase() === i18n.language
			)?.Value

			createNewContract({
				...contract,
				email: contract.email ?? '',
				ssn: contract.ssn ?? '',
				firstName: contract.firstName ?? '',
				lastName: contract.lastName ?? '',
				phone: contract.phone ?? '',
				invoiceType: contract.invoiceType ?? '',
				noAds: contract.noAds ?? true,
				contractStartDate: dayjs(contract.contractStartDate).format('YYYY-MM-DDT00:00:00[Z]'),
				newContractType: contract.newContractType ?? $Enums.NewContractType.newContract,
				productId: product.ID,
				creationDate: dayjs().format(),
				productName: productName ?? contract.productName ?? '',
				language: i18n?.language ?? 'fi',
				isLockIn: contract.isLockIn ?? false,
				lockInName: contract.lockInName ?? null,
				lockInPeriod: contract.lockInPeriod ?? null,
				lockInProductId: contract.lockInProductId ?? null,
				lockInHourlyPrice: contract?.lockInHourlyPrice ?? null,
				lockInMonthlyPrice: contract?.lockInMonthlyPrice ?? null,
				lockInValidFrom: contract.lockInValidFrom ?? null,
				lockInValidTo: contract.lockInValidTo ?? null,
				lockInRatio: contract?.lockInRatio ?? null,
				isContractUpdate: contract.isContractUpdate ?? false,
				isMobile: true,
			})
		}
	}

	const { t } = useTranslation('modals_createContract', { keyPrefix: 'termsAndConditionsStep' })

	return (
		<div className='container text-text-dark dark:text-white'>
			<span>{t('termsAndConditions1')}</span>
			<span>{t('termsAndConditions2')}</span>
			<span>{t('termsAndConditions3')}</span>
			<span>{t('termsAndConditions4')}</span>

			<span> {t('termsAndConditions5')}</span>
			<span> {t('termsAndConditions6')}</span>

			<div className='link-container flex flex-col gap-2'>
				<Link href='https://vihreaenergia.fi/tietosuoja/'> {t('privacyPolicy')}</Link>
				<Link href='https://vihreaenergia.fi/wp-content/uploads/2024/05/sahkonmyyntiehdot_sme_2014.pdf'>
					{t('terms')}
				</Link>
			</div>

			<CheckboxInput
				isChecked={contract?.noAds ?? true}
				onChange={() => setContract('noAds', !contract?.noAds)}
				label={t('noAds')}
			/>
			<CheckboxInput
				isChecked={termsAccepted ?? false}
				onChange={() => setTermsAccepted(!termsAccepted)}
				label={t('acceptTerms')}
			/>

			<div className='submit-contract'>
				<Button disabled={!termsAccepted || isLoading} onClick={onComplete ?? onCreateContract}>
					{t('createContract')}
				</Button>
			</div>
		</div>
	)
}

export default TermsAndConditionsStep
